import type { BookingsModuleConfig, DoorPanelConfig, Weekday } from '@/types/bookings'
import type { ApiError } from './bookingsUtils'

/** What a Reservas space is born with (PROPOSTA-COWORKING-RESERVAS section 7.5): Monday to Friday, 08:00 to 18:00. */
export const defaultBookingsConfig = (): BookingsModuleConfig => ({
  slotMinutes: 30,
  minDurationMinutes: 30,
  maxDurationMinutes: 240,
  minNoticeMinutes: 0,
  maxAdvanceDays: 30,
  showBookerName: false,
  openingHours: Object.fromEntries(
    (['mon', 'tue', 'wed', 'thu', 'fri'] as Weekday[]).map((d) => [d, [{ from: '08:00', to: '18:00' }]])
  )
})

/** The door panel's defaults (PROPOSTA section 13b); the API fills the same ones in. */
export const DOOR_PANEL_DEFAULTS: DoorPanelConfig = {
  releaseOnNoCheckIn: false,
  graceMinutes: 10,
  showStartButton: false,
  quickDurations: [15, 30, 60],
  showBookerFirstName: true,
  showTitle: false
}

export const doorPanelOf = (config: BookingsModuleConfig): DoorPanelConfig => ({ ...DOOR_PANEL_DEFAULTS, ...(config.doorPanel ?? {}) })

/** "15, 30, 60" into `[15, 30, 60]`; anything that is not a list of whole numbers comes back as `null`. */
export const parseDurations = (text: string): number[] | null => {
  const parts = text.split(/[\s,;]+/).filter(Boolean)
  if (!parts.length) return null
  const numbers = parts.map(Number)
  return numbers.every((n) => Number.isInteger(n)) ? numbers : null
}

export type BookingsConfigErrors = Partial<Record<keyof BookingsModuleConfig, string>>

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/

/** Same bounds as the API (`bookingsConfig.service`); the server stays the judge. */
export const validateBookingsConfig = (
  config: BookingsModuleConfig,
  t: (key: string, vars?: Record<string, unknown>) => string
): BookingsConfigErrors | null => {
  const errors: BookingsConfigErrors = {}
  const int = (v: unknown, min: number, max: number) => Number.isInteger(v) && (v as number) >= min && (v as number) <= max
  if (!int(config.slotMinutes, 5, 1440)) errors.slotMinutes = t('bookings:config.errors.minutes', { min: 5, max: 1440 })
  if (!int(config.minDurationMinutes, 5, 1440)) errors.minDurationMinutes = t('bookings:config.errors.minutes', { min: 5, max: 1440 })
  if (!int(config.maxDurationMinutes, 5, 10080)) errors.maxDurationMinutes = t('bookings:config.errors.minutes', { min: 5, max: 10080 })
  else if (!errors.minDurationMinutes && config.maxDurationMinutes < config.minDurationMinutes) {
    errors.maxDurationMinutes = t('bookings:config.errors.maxBelowMin')
  }
  if (!int(config.minNoticeMinutes, 0, 525600)) errors.minNoticeMinutes = t('bookings:config.errors.minutes', { min: 0, max: 525600 })
  if (!int(config.maxAdvanceDays, 1, 730)) errors.maxAdvanceDays = t('bookings:config.errors.days', { min: 1, max: 730 })
  const ranges = Object.values(config.openingHours ?? {}).flat()
  if (ranges.some((r) => !r || !TIME.test(r.from) || !TIME.test(r.to) || r.to <= r.from)) {
    errors.openingHours = t('bookings:hours.invalid')
  }
  const panel = config.doorPanel
  if (panel) {
    const grace = panel.graceMinutes
    const list = panel.quickDurations
    if (grace !== undefined && !int(grace, 1, 120)) errors.doorPanel = t('roomPanel:config.errors.grace', { min: 1, max: 120 })
    else if (list !== undefined && (!Array.isArray(list) || list.length < 1 || list.length > 6 || !list.every((n) => int(n, 5, 480)) || new Set(list).size !== list.length)) {
      errors.doorPanel = t('roomPanel:config.errors.durations', { min: 5, max: 480 })
    }
  }
  return Object.keys(errors).length ? errors : null
}

/** `{ param: 'moduleConfig.bookings.slotMinutes', message }` into per-field messages. */
export const bookingsConfigApiErrors = (err: unknown): BookingsConfigErrors | null => {
  const list = (err as ApiError | null)?.response?.data?.errors ?? []
  const out: BookingsConfigErrors = {}
  for (const e of list) {
    const match = e.param?.match(/^moduleConfig\.bookings\.(\w+)/)
    if (match && e.message) (out as Record<string, string>)[match[1]] = e.message
  }
  return Object.keys(out).length ? out : null
}
